<template>
  <van-popup v-model="visible" position="bottom" round closeable :style="{ maxHeight: '90%' }" @closed="reset">
    <div class="sheet">
      <div class="sheet-title">快速記錄</div>

      <div v-if="!mode" class="type-grid">
        <button v-for="t in types" :key="t.key" type="button" class="type-btn" @click="pick(t.key)">
          <span class="icon">{{ t.icon }}</span>
          <span>{{ t.label }}</span>
        </button>
      </div>

      <div v-else class="form-area">
        <div class="auto-meta">
          <span class="tag-chip">🕒 {{ nowLabel }}</span>
          <span class="tag-chip">📍 {{ location.label }}</span>
        </div>

        <template v-if="mode === 'photo' || mode === 'physical'">
          <input type="file" accept="image/*" @change="onFile" class="file-input">
          <div v-if="photoPreview" class="photo-preview"><img :src="photoPreview" alt="preview"></div>
          <van-field v-if="mode === 'physical'" label="種類">
            <template #input>
              <select v-model="physicalSubtype" class="native-select">
                <option v-for="s in physicalSubtypes" :key="s" :value="s">{{ s }}</option>
              </select>
            </template>
          </van-field>
        </template>

        <van-field v-model="text" type="textarea" rows="2" placeholder="想記下什麼？（選填）" />

        <div v-if="mode === 'note'" class="mood-block">
          <div class="section-title">心情（選填）</div>
          <MoodPicker v-model="mood" />
        </div>

        <PointPicker v-model="pointId" :trip-id="tripId" />

        <div class="section-title">誰看得到</div>
        <VisibilitySelect v-model="visibility" />

        <div class="actions">
          <van-button block type="default" @click="mode = null">返回</van-button>
          <van-button block type="primary" @click="submit">儲存</van-button>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<script>
import { mapActions } from 'vuex'
import dayjs from 'dayjs'
import MoodPicker from '@/components/moments/MoodPicker.vue'
import PointPicker from '@/components/places/PointPicker.vue'
import VisibilitySelect from '@/components/common/VisibilitySelect.vue'
import { getMockLocation } from '@/utils/geo'
import { Toast } from 'vant'

const TYPES = [
  { key: 'photo', icon: '📷', label: '照片' },
  { key: 'note', icon: '📝', label: '文字 / 心情' },
  { key: 'discovery', icon: '✨', label: '意外發現' },
  { key: 'expense', icon: '💰', label: '記帳' },
  { key: 'physical', icon: '🎫', label: '實體回憶' }
]

const PHYSICAL_SUBTYPES = ['票根', '明信片', '收據', '貼紙', '紀念品', '手寫文字', '其他']

export default {
  name: 'QuickCaptureSheet',
  components: { MoodPicker, PointPicker, VisibilitySelect },
  props: {
    tripId: { type: String, required: true },
    creatorId: { type: String, required: true }
  },
  data () {
    return {
      visible: false,
      mode: null,
      types: TYPES,
      physicalSubtypes: PHYSICAL_SUBTYPES,
      physicalSubtype: PHYSICAL_SUBTYPES[0],
      text: '',
      mood: null,
      pointId: null,
      visibility: 'trip',
      photoPreview: null,
      location: getMockLocation()
    }
  },
  computed: {
    nowLabel () {
      return dayjs().format('MM/DD HH:mm')
    }
  },
  methods: {
    ...mapActions({ createMoment: 'moments/createMoment', queueForSync: 'ui/queueForSync' }),
    open () {
      this.visible = true
      this.location = getMockLocation()
    },
    pick (key) {
      if (key === 'discovery') {
        this.visible = false
        this.$emit('open-discovery')
        return
      }
      if (key === 'expense') {
        this.visible = false
        this.$emit('open-expense')
        return
      }
      this.mode = key
    },
    onFile (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => { this.photoPreview = reader.result }
      reader.readAsDataURL(file)
    },
    submit () {
      const id = this.createMoment({
        tripId: this.tripId,
        creatorId: this.creatorId,
        kind: this.mode === 'physical' ? 'physical' : 'normal',
        text: this.text,
        media: this.photoPreview ? [this.photoPreview] : [],
        mood: this.mood,
        pointId: this.pointId,
        location: { lat: this.location.lat, lng: this.location.lng, label: this.location.label },
        visibility: this.visibility,
        physicalSubtype: this.mode === 'physical' ? this.physicalSubtype : null
      })
      this.queueForSync(`新增 ${this.mode === 'physical' ? '實體回憶' : this.mode === 'photo' ? '照片' : '記錄'}`)
      Toast.success('已記錄！')
      this.visible = false
      this.$emit('created', id)
    },
    reset () {
      this.mode = null
      this.text = ''
      this.mood = null
      this.pointId = null
      this.visibility = 'trip'
      this.photoPreview = null
    }
  }
}
</script>

<style scoped>
.sheet { padding-bottom: 16px; }
.sheet-title { padding: 16px; font-weight: 700; font-size: 16px; border-bottom: 1px solid var(--color-border); }
.type-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; padding: 18px 16px; }
.type-btn {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 16px 4px; border-radius: var(--radius-md); border: 1px solid var(--color-border); background: #fff;
  font-size: 12px; color: var(--color-text);
}
.icon { font-size: 24px; }
.form-area { padding: 12px 0; }
.auto-meta { display: flex; gap: 8px; padding: 0 16px 10px; }
.file-input { margin: 0 16px 10px; display: block; }
.photo-preview { margin: 0 16px 10px; }
.photo-preview img { max-width: 100%; border-radius: var(--radius-md); max-height: 180px; object-fit: cover; }
.native-select { border: none; font-size: 14px; width: 100%; background: transparent; color: var(--color-text); }
.mood-block { padding: 8px 16px; }
.section-title { padding: 0 16px; }
.actions { display: flex; gap: 10px; padding: 16px; }
</style>
